import { Box, Tooltip } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { getExceptionalStatusTranslationKey } from "src/utils/getExceptionalStatusTranslationKey";
import { memo, useMemo } from "react";
import { LocationExceptionalStatusRead } from "src/store/api/generatedApi";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useTimelineZoom } from "../scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { useExceptionDraftOptional } from "./contexts/ExceptionDraftContext";

dayjs.extend(utc);

const STATUS_STYLES = {
  missing: { bgcolor: "common.black", opacity: 0.5 },
  ignorable: { bgcolor: "grey.500", opacity: 0.4 },
  bad_labels: { bgcolor: "warning.main", opacity: 0.4 },
  broken_scale_weights: { bgcolor: "warning.main", opacity: 0.4 },
} as const;

export const ManualReviewVideoExceptionalBars = ({
  videoStart,
}: {
  videoStart: Dayjs;
}) => {
  const draft = useExceptionDraftOptional();
  const exceptionalStatuses = useMemo(
    () => draft?.exceptionalStatuses ?? [],
    [draft]
  );
  const state = useVideoControlState();
  const context = useTimelineZoom();
  const duration = context?.duration ?? state.duration;
  const visibleStart = context?.visibleStart ?? videoStart;
  const visibleEnd = context?.visibleEnd ?? videoStart.add(duration, "second");

  const visibleStatuses = useMemo(
    () =>
      exceptionalStatuses.filter((status) => {
        const statusStart = dayjs.utc(status.started_at);
        const statusEnd = dayjs.utc(status.ended_at);
        return (
          statusEnd.isAfter(visibleStart) && statusStart.isBefore(visibleEnd)
        );
      }),
    [exceptionalStatuses, visibleStart, visibleEnd]
  );

  if (visibleStatuses.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 8,
        left: 0,
        right: 0,
        height: 6,
      }}
    >
      {visibleStatuses.map((status) => (
        <ExceptionalStatusBar
          key={status.id}
          status={status}
          visibleStart={visibleStart}
          duration={duration}
        />
      ))}
    </Box>
  );
};

const ExceptionalStatusBar = memo(
  ({
    status,
    visibleStart,
    duration,
  }: {
    status: LocationExceptionalStatusRead;
    visibleStart: Dayjs;
    duration: number;
  }) => {
    const { t } = useTenantTranslation();
    const statusStart = dayjs.utc(status.started_at);
    const statusEnd = dayjs.utc(status.ended_at);

    const rawLeft =
      (statusStart.diff(visibleStart, "second", true) / duration) * 100;
    const rawRight =
      (statusEnd.diff(visibleStart, "second", true) / duration) * 100;
    const leftPercent = Math.max(0, rawLeft);
    const widthPercent = Math.min(100 - leftPercent, rawRight - leftPercent);

    const style = STATUS_STYLES[status.status];

    const tooltipTitle = useMemo(() => {
      const start = statusStart.format("YYYY-MM-DD HH:mm");
      const end = statusEnd.format("YYYY-MM-DD HH:mm");
      const statusLabel = t(getExceptionalStatusTranslationKey(status.status));
      const detail = status.note
        ? `${statusLabel}: ${status.note}`
        : statusLabel;
      return `${start} – ${end} UTC\n${detail}`;
    }, [statusStart, statusEnd, status.note, status.status, t]);

    return (
      <Tooltip title={tooltipTitle} arrow>
        <Box
          sx={{
            position: "absolute",
            left: `${leftPercent}%`,
            width: `${Math.max(widthPercent, 0.5)}%`,
            top: 0,
            height: "100%",
            bgcolor: style.bgcolor,
            opacity: style.opacity,
            borderRadius: 0.5,
          }}
        />
      </Tooltip>
    );
  }
);
ExceptionalStatusBar.displayName = "ExceptionalStatusBar";
